import { produce } from 'immer';
import { DEPLOY_STATUS_UPDATE, DEPLOY_HISTORY_REQUEST, DEPLOY_HISTORY_SUCCESS, DEPLOY_HISTORY_FAILURE } from '../actions/deployStatus';

/**
 * Deploy status — the header pill and the Deploys page.
 * See decap-turbo/docs/deploy-status-plan.md §A8.
 *
 * Distinct from the older `deploys` slice, which is deploy PREVIEW links for
 * unpublished editorial-workflow entries. Previews answer "what would this
 * look like"; this answers "has what I published actually shipped".
 */

const defaultState = {
  pendingCount: 0,
  latest: null,
  deployments: [],
  branch: null,
  entryLabels: {},
  isFetching: false,
  error: null,
  supported: false,
  pageEnabled: false,
  loaded: false
};

/**
 * The rows that describe the site itself, in the order they arrived.
 *
 * A row naming no branch is kept: some hosts report none, and dropping those
 * would leave such a site permanently unable to say anything about itself.
 * Only a row that names a different branch is excluded.
 */
export function onSiteBranch(deployments, branch) {
  if (!branch) {
    return deployments;
  }
  return deployments.filter(row => !row.branch || row.branch === branch);
}
const deployStatus = produce((state, action) => {
  switch (action.type) {
    case DEPLOY_STATUS_UPDATE:
      {
        const {
          pendingCount,
          latest,
          supported,
          pageEnabled,
          branch
        } = action.payload;
        state.pendingCount = pendingCount;
        if (branch !== undefined) {
          state.branch = branch;
        }
        // A poll that returns nothing must not erase what we already knew: the
        // window it reads is bounded by the oldest pending save, so an older
        // deploy legitimately falls out of it.
        if (latest) {
          state.latest = latest;
        }
        if (supported !== undefined) {
          state.supported = supported;
        }
        if (pageEnabled !== undefined) {
          state.pageEnabled = pageEnabled;
        }
        break;
      }
    case DEPLOY_HISTORY_REQUEST:
      state.isFetching = true;
      state.error = null;
      break;
    case DEPLOY_HISTORY_SUCCESS:
      {
        state.isFetching = false;
        state.loaded = true;
        state.deployments = action.payload.deployments;
        // The history read is deliberately unscoped — showing which branch a
        // deploy went to is half the point of the table. `latest` is not: it is
        // "the state of the site", and a branch deploy of an unpublished entry
        // is not the state of the site.
        state.latest = onSiteBranch(action.payload.deployments, state.branch)[0] ?? state.latest;
        state.entryLabels = {};
        for (const commit of action.payload.commits ?? []) {
          if (commit.entry_label) {
            state.entryLabels[commit.commit_sha] = commit.entry_label;
          }
        }
        break;
      }
    case DEPLOY_HISTORY_FAILURE:
      state.isFetching = false;
      // `loaded` deliberately stays false: an empty list after a failure means
      // "we do not know", and the page must say so rather than claim the site
      // has never deployed.
      state.error = action.payload.error;
      break;
  }
}, defaultState);

/**
 * Auto-hide, and the whole of §A7's default: the nav item and the route exist
 * only once this site has actually produced deploy information. A site whose
 * host reports nothing — every Netlify site without the webhook of §A6 —
 * should look like a CMS without the feature, not like one with a permanently
 * empty page.
 */
export function selectDeployStatusVisible(state) {
  return state.supported && state.pageEnabled && (
  // `deployments` as well as `latest`, because `latest` is scoped to the
  // site's branch: a site whose host only reports branch deploys has real
  // deploy information and nothing that is "live", and the page is exactly
  // where someone would go to find out why.
  state.latest !== null || state.deployments.length > 0 || state.pendingCount > 0);
}
export default deployStatus;